import React from 'react';
import {
  ClockIcon,
  TruckIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleIconSolid,
  PlayCircleIcon as PlayCircleIconSolid,
} from '@heroicons/react/24/solid';
import type { EnrichedCase } from '../../types';
import { CASE_STATUS_LABELS, REMARKS_LABELS } from '../../constants/dashboard';
import { TimeDisplay } from './TimeDisplay';

interface CaseTimelineCardProps {
  case: EnrichedCase & {
    vehicleName: string;
    pickupPartnerName: string;
    deliveryPartnerName: string;
    pickupPrefecture: string; // FL-188: 地域
    vehiclePlateNumber: string; // FL-188: 車体番号
    timeInfo: {
      startTime: string;
      endTime: string;
      label: string;
    };
  };
  onStatusUpdate?: (caseId: string, newStatus: 'in_transit' | 'completed') => void;
}

/**
 * 案件タイムラインカード
 * スケジュール一覧で各案件を表示するカード
 */
export const CaseTimelineCard: React.FC<CaseTimelineCardProps> = ({ case: caseData }) => {
  const getStatusIcon = () => {
    switch (caseData.status) {
      case 'completed':
        return <CheckCircleIconSolid className='h-5 w-5 text-green-500' />;
      case 'in_transit':
        return <PlayCircleIconSolid className='h-5 w-5 text-yellow-500' />;
      default:
        return <ClockIcon className='h-5 w-5 text-gray-400 dark:text-gray-500' />;
    }
  };

  const getStatusText = () => {
    return CASE_STATUS_LABELS[caseData.status] || '不明';
  };

  const isCompleted = caseData.status === 'completed';
  const cardOpacity = isCompleted ? 'opacity-75' : '';
  const isUnassigned = !caseData.assignedLandTransporterId;

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${cardOpacity}`}
    >
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-3'>
          {getStatusIcon()}
          <div>
            {/* 時間表示: 未割り当て時のみ表示 */}
            {isUnassigned && (
              <TimeDisplay
                pickupTimeStart={caseData.pickupTimeStart}
                pickupTimeEnd={caseData.pickupTimeEnd}
                deliveryTime={caseData.deliveryTime}
                textSize='text-xs'
                spacing='space-y-0.5'
              />
            )}
            <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>{getStatusText()}</div>
          </div>
        </div>

        {/* 優先度表示 */}
        {caseData.remarks?.includes(REMARKS_LABELS.URGENT_KEYWORD) && (
          <ExclamationTriangleIcon className='h-5 w-5 text-red-500' />
        )}
      </div>

      <div className='space-y-2'>
        <div className='flex items-center gap-2 text-sm text-gray-900 dark:text-white'>
          <TruckIcon className='h-4 w-4 text-gray-400 dark:text-gray-500' />
          {caseData.vehicleName}
        </div>
        {/* FL-188 + FL-191: 地域・ナンバープレート・車体番号 */}
        <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300'>
          <MapPinIcon className='h-4 w-4 text-gray-400 dark:text-gray-500' />
          <span>{caseData.pickupPrefecture}</span>
          <span className='text-gray-400 dark:text-gray-600'>|</span>
          <span className='font-mono'>{caseData.vehiclePlateNumber}</span>
          {caseData.chassisNumber && (
            <>
              <span className='text-gray-400 dark:text-gray-600'>|</span>
              <span className='font-mono text-xs'>車体: {caseData.chassisNumber}</span>
            </>
          )}
        </div>
        <div className='flex items-start gap-2 text-sm text-gray-900 dark:text-white'>
          <MapPinIcon className='h-4 w-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0' />
          <div>
            <div className='font-medium'>引取: {caseData.pickupPartnerName}</div>
            <div className='font-medium'>搬入: {caseData.deliveryPartnerName}</div>
          </div>
        </div>
      </div>

      {/* 特記事項がある場合 */}
      {caseData.remarks && (
        <div className='mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-700'>
          <div className='text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1'>
            {REMARKS_LABELS.TITLE}
          </div>
          <div className='text-sm text-yellow-700 dark:text-yellow-300'>{caseData.remarks}</div>
        </div>
      )}
    </div>
  );
};
